import { ElMessage, ElMessageBox } from 'element-plus'

import { i18n } from '@/langs'

import * as consts from './consts'

const SIGN_REGEXP = /([yMdhsm])(\1*)/g
const DEFAULT_PATTERN = consts.DEFAULT_DATE_FORMAT

/**
 * Pads a string or number with leading zeros to reach the specified length
 * @param s - The string or number to pad
 * @param len - The target length
 * @returns The padded string
 * @example
 * padding(5, 3) // '005'
 * padding('12', 4) // '0012'
 */
export const padding = (s: string | number, len: number): string => {
  len = len - (s + '').length
  for (let i = 0; i < len; i++) {
    s = '0' + s
  }
  return s as string
}

/**
 * Gets the value of a specified parameter from the URL query string
 * @param name - The name of the query parameter
 * @returns The parameter value, or an empty string if it doesn't exist
 * @example
 * URL: https://example.com?name=John&age=25
 * getQueryStringByName('name') // 'John'
 * getQueryStringByName('age') // '25'
 * getQueryStringByName('notexist') // ''
 */
export const getQueryStringByName = (name: string): string => {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  const r = window.location.search.slice(1).match(reg)
  let context = ''
  if (r != null && r[2] !== undefined) {
    context = r[2]
  }

  return context == null || context === '' || context === 'undefined' ? '' : context
}

/**
 * Date formatting utilities
 */
export const formatDate = {
  /**
   * Formats a date according to the specified pattern
   * @param date - The date to format (Date object, date string, null, or undefined)
   * @param pattern - The format pattern (default: DEFAULT_DATE_FORMAT). Supported tokens: y (year), M (month), d (day), h (hour), m (minute), s (second), w (weekday)
   * @returns The formatted date string, or empty string if date is null/undefined
   * @example
   * formatDate.format(new Date(), 'yyyy-MM-dd') // '2024-01-15'
   * formatDate.format(new Date(), 'yyyy-MM-dd hh:mm:ss') // '2024-01-15 14:30:45'
   */
  format: (date: Date | string | null | undefined, pattern?: string): string => {
    if (!date) return ''
    let dateObj: Date
    if (typeof date === 'string') {
      dateObj = new Date(date)
    } else {
      dateObj = date
    }
    const finalPattern = pattern || DEFAULT_PATTERN
    return finalPattern.replace(SIGN_REGEXP, ($0: string) => {
      switch ($0.charAt(0)) {
        case 'y':
          return padding(dateObj.getFullYear(), $0.length)
        case 'M':
          return padding(dateObj.getMonth() + 1, $0.length)
        case 'd':
          return padding(dateObj.getDate(), $0.length)
        case 'w':
          return String(dateObj.getDay() + 1)
        case 'h':
          return padding(dateObj.getHours(), $0.length)
        case 'm':
          return padding(dateObj.getMinutes(), $0.length)
        case 's':
          return padding(dateObj.getSeconds(), $0.length)
        default:
          return $0
      }
    })
  },
  /**
   * Parses a date string according to the specified pattern
   * @param dateString - The date string to parse
   * @param pattern - The format pattern that matches the date string
   * @returns The parsed Date object, or null if parsing fails
   * @example
   * formatDate.parse('2024-01-15', 'yyyy-MM-dd') // Date object
   * formatDate.parse('2024-01-15 14:30:45', 'yyyy-MM-dd hh:mm:ss') // Date object
   */
  parse: (dateString: string, pattern: string): Date | null => {
    const match1 = pattern.match(SIGN_REGEXP)
    const match2 = dateString.match(/(\d)+/g)
    if (match1 && match2 && match1.length === match2.length) {
      const _date = new Date(1970, 0, 1)
      for (let i = 0; i < match1.length; i++) {
        const match2Value = match2[i]
        const sign = match1[i]
        if (!match2Value || !sign) continue
        const _int = parseInt(match2Value, 10)
        switch (sign.charAt(0)) {
          case 'y':
            _date.setFullYear(_int)
            break
          case 'M':
            _date.setMonth(_int - 1)
            break
          case 'd':
            _date.setDate(_int)
            break
          case 'h':
            _date.setHours(_int)
            break
          case 'm':
            _date.setMinutes(_int)
            break
          case 's':
            _date.setSeconds(_int)
            break
        }
      }
      return _date
    }
    return null
  }
}

/**
 * Formats a boolean value to a translation key
 * @param val - The boolean value to format
 * @returns 'common.yes' for true, 'common.no' for false, or the original value for other types
 * @example
 * formatBoolean(true) // 'common.yes'
 * formatBoolean(false) // 'common.no'
 * formatBoolean('other') // 'other'
 */
export const formatBoolean = (val: boolean | unknown): string | unknown => {
  if (val === true) {
    return i18n.global.t('common.yes')
  } else if (val === false) {
    return i18n.global.t('common.no')
  } else {
    return val
  }
}

/**
 * Formats a date to yyyyMMdd format
 * @param dateVal - The date to format
 * @returns The formatted date string in yyyyMMdd format
 * @example
 * yyyymmdd(new Date(2024, 0, 15)) // '20240115'
 */
export const yyyymmdd = (dateVal: Date): string => {
  const yyyy = dateVal.getFullYear()
  const mm = dateVal.getMonth() < 9 ? '0' + (dateVal.getMonth() + 1) : String(dateVal.getMonth() + 1) // getMonth() is zero-based
  const dd = dateVal.getDate() < 10 ? '0' + dateVal.getDate() : String(dateVal.getDate())
  return ''.concat(String(yyyy)).concat(String(mm)).concat(String(dd))
}

/**
 * Formats a date to yyyyMMddHHmm format
 * @param dateVal - The date to format
 * @returns The formatted date string in yyyyMMddHHmm format
 * @example
 * yyyymmddhhmm(new Date(2024, 0, 15, 14, 30)) // '202401151430'
 */
export const yyyymmddhhmm = (dateVal: Date): string => {
  const yyyy = dateVal.getFullYear()
  const mm = dateVal.getMonth() < 9 ? '0' + (dateVal.getMonth() + 1) : String(dateVal.getMonth() + 1) // getMonth() is zero-based
  const dd = dateVal.getDate() < 10 ? '0' + dateVal.getDate() : String(dateVal.getDate())
  const hh = dateVal.getHours() < 10 ? '0' + dateVal.getHours() : String(dateVal.getHours())
  const min = dateVal.getMinutes() < 10 ? '0' + dateVal.getMinutes() : String(dateVal.getMinutes())
  return ''.concat(String(yyyy)).concat(String(mm)).concat(String(dd)).concat(String(hh)).concat(String(min))
}

/**
 * Formats a date to yyyyMMddHHmmss format
 * @param dateVal - The date to format
 * @returns The formatted date string in yyyyMMddHHmmss format
 * @example
 * yyyymmddhhmmss(new Date(2024, 0, 15, 14, 30, 45)) // '20240115143045'
 */
export const yyyymmddhhmmss = (dateVal: Date): string => {
  const yyyy = dateVal.getFullYear()
  const mm = dateVal.getMonth() < 9 ? '0' + (dateVal.getMonth() + 1) : String(dateVal.getMonth() + 1) // getMonth() is zero-based
  const dd = dateVal.getDate() < 10 ? '0' + dateVal.getDate() : String(dateVal.getDate())
  const hh = dateVal.getHours() < 10 ? '0' + dateVal.getHours() : String(dateVal.getHours())
  const min = dateVal.getMinutes() < 10 ? '0' + dateVal.getMinutes() : String(dateVal.getMinutes())
  const ss = dateVal.getSeconds() < 10 ? '0' + dateVal.getSeconds() : String(dateVal.getSeconds())
  return ''
    .concat(String(yyyy))
    .concat(String(mm))
    .concat(String(dd))
    .concat(String(hh))
    .concat(String(min))
    .concat(String(ss))
}

/**
 * Shows a success message using Element Plus message component
 * @param message - The success message to display
 * @example
 * showSuccess('Operation completed successfully')
 */
export const showSuccess = (message: string): void => {
  ElMessage({
    message: message,
    type: 'success'
  })
}

/**
 * Shows a warning message using Element Plus message component
 * @param message - The warning message to display
 * @example
 * showWarning('Please check your input')
 */
export const showWarning = (message: string): void => {
  ElMessage({
    message: message,
    type: 'warning'
  })
}

/**
 * Standard API error response shape from the backend.
 * Always has `code` and `message`. `details` is only present when backend DEBUG=true.
 */
interface ApiErrorBody {
  code?: string
  message?: string
  details?: Record<string, unknown>
  detail?: unknown
}

interface AxiosLikeError {
  response?: { data?: ApiErrorBody; status?: number }
  message?: string
}

const isGenericAxiosMessage = (msg: string): boolean => {
  const trimmed = msg.trim()
  return (
    /^Request failed with status code \d+$/i.test(trimmed) ||
    /^AxiosError:/i.test(trimmed) ||
    trimmed === 'Network Error'
  )
}

const normalizeDetail = (detail: unknown): string | null => {
  if (detail == null) return null
  if (typeof detail === 'string') {
    const trimmed = detail.trim()
    return trimmed || null
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map(item => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'msg' in item) {
          const err = item as { msg?: string; loc?: unknown[] }
          const loc = Array.isArray(err.loc) ? err.loc.map(String).join(' -> ') : ''
          return loc ? `${loc}: ${err.msg ?? ''}` : (err.msg ?? String(item))
        }
        return String(item)
      })
      .filter(Boolean)
    return parts.length ? parts.join('; ') : null
  }
  if (typeof detail === 'object') return JSON.stringify(detail)
  return String(detail)
}

const statusFallbackMessage = (status?: number): string => {
  switch (status) {
    case 401:
      return i18n.global.t('api.unauthorized')
    case 403:
      return i18n.global.t('api.forbidden')
    case 404:
      return i18n.global.t('api.not_found')
    case 429:
      return i18n.global.t('api.rate_limited')
    default:
      return i18n.global.t('api.request_failed')
  }
}

const resolveApiMessage = (body: ApiErrorBody, status?: number): string => {
  const detailText = normalizeDetail(body.detail)
  const rawMessage = typeof body.message === 'string' ? body.message.trim() : ''
  let message = rawMessage || detailText || ''
  if (detailText && rawMessage && rawMessage === String(body.detail)) {
    message = detailText
  }
  if (!message || isGenericAxiosMessage(message)) {
    return statusFallbackMessage(status)
  }
  return message
}

/**
 * Extracts structured error info from an axios error / unknown error.
 * Returns { code, message, details } where details is only set
 * when the backend responded with DEBUG info.
 */
export const extractApiError = (
  error: unknown
): { code: string; message: string; details: Record<string, unknown> | null } => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axErr = error as AxiosLikeError
    const status = axErr.response?.status
    const body = axErr.response?.data
    if (body && typeof body === 'object') {
      const code = body.code ?? `HTTP_${status ?? 'UNKNOWN'}`
      const message = resolveApiMessage(body, status)
      const details = body.details ?? null
      return { code, message, details }
    }
    const axMsg = axErr.message?.trim() ?? ''
    if (axMsg && !isGenericAxiosMessage(axMsg)) {
      return { code: `HTTP_${status ?? 'UNKNOWN'}`, message: axMsg, details: null }
    }
    return { code: `HTTP_${status ?? 'UNKNOWN'}`, message: statusFallbackMessage(status), details: null }
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: string }).message
    if (msg && !isGenericAxiosMessage(msg)) {
      return { code: 'CLIENT_ERROR', message: msg, details: null }
    }
    return { code: 'CLIENT_ERROR', message: i18n.global.t('api.request_failed'), details: null }
  }
  return { code: 'UNKNOWN_ERROR', message: i18n.global.t('api.request_failed'), details: null }
}

/**
 * Extracts a human-readable error message from any error shape.
 * Kept for backward compatibility — callers that just need a string.
 */
export const getErrorMessage = (error: unknown): string => {
  const { message } = extractApiError(error)
  return message
}

/**
 * Shows an error message.
 *
 * - When the backend sent DEBUG details, uses `ElMessage` with an HTML body
 *   showing the error code and technical detail so developers can diagnose fast.
 * - Otherwise shows a clean user-friendly message.
 */
export const showError = (error: unknown): void => {
  const { code, message, details } = extractApiError(error)

  if (details) {
    const techDetail =
      (details.technical_detail as string) ?? (details.fields ? (details.fields as string[]).join('; ') : '')

    const htmlMsg = techDetail
      ? `<strong>[${code}]</strong> ${message}<br/><span style="color:#909399;font-size:12px">${techDetail}</span>`
      : `<strong>[${code}]</strong> ${message}`

    ElMessage({ type: 'error', dangerouslyUseHTMLString: true, message: htmlMsg, duration: 6000, showClose: true })
  } else {
    ElMessage({ type: 'error', message, duration: 4000, showClose: true })
  }
}

/** Info modal when login/SSO succeeded but the account awaits administrator approval. */
export const showRegistrationPendingDialog = async (): Promise<void> => {
  await ElMessageBox.alert(
    i18n.global.t('signin.registration_pending_body'),
    i18n.global.t('signin.registration_pending_title'),
    {
      confirmButtonText: i18n.global.t('common.confirm'),
      type: 'info'
    }
  )
}

/**
 * Common validation rules for form validation
 */
export const commonValidation = {
  /**
   * Creates a required field validation rule
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object with required: true
   */
  require(msg: string) {
    return {
      required: true,
      message: msg,
      trigger: 'blur'
    }
  },
  /**
   * Creates an integer validation rule
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object that only allows integers
   */
  integer(msg: string) {
    return {
      required: true,
      pattern: /^[0-9]+$/,
      message: msg
    }
  },
  /**
   * Creates a float validation rule (allows up to 4 decimal places)
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object that only allows floating point numbers
   */
  float(msg: string) {
    return {
      required: true,
      pattern: /^[0-9]+(\.[0-9]{1,4})?$/,
      message: msg
    }
  },
  /**
   * Creates a minimum length validation rule
   * @param length - The minimum required length
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object with min length constraint
   */
  minLength(length: number, msg: string) {
    return {
      min: length,
      message: msg
    }
  },
  /**
   * Creates a maximum length validation rule
   * @param length - The maximum allowed length
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object with max length constraint
   */
  maxLength(length: number, msg: string) {
    return {
      max: length,
      message: msg
    }
  },
  /**
   * Creates a length range validation rule
   * @param min - The minimum allowed length
   * @param max - The maximum allowed length
   * @param msg - The error message to display when validation fails
   * @returns A validation rule object with min and max length constraints
   */
  betweenLength(min: number, max: number, msg: string) {
    return {
      min: min,
      max: max,
      message: msg
    }
  }
}

/**
 * Interface for date range items that may come from date picker components
 */
interface DateRangeItem {
  $d?: Date
  setHours?: (hours: number, minutes: number, seconds: number, ms: number) => number
}

/**
 * Converts a date range array to a standardized Date array
 * Handles various date formats including date picker objects with $d property
 * Sets the end date to 23:59:59.999 for inclusive date range queries
 * @param searchDateRange - A tuple of two date range items (start and end dates)
 * @returns An array of two Date objects [startDate, endDate] where endDate is set to end of day
 * @example
 * getDateRange([{ $d: new Date('2024-01-01') }, { $d: new Date('2024-01-31') }])
 * Returns [Date('2024-01-01'), Date('2024-01-31 23:59:59.999')]
 */
export const getDateRange = (searchDateRange: [DateRangeItem, DateRangeItem]): Date[] => {
  const dateRange: Date[] = []

  // Handle start date
  if (typeof searchDateRange[0].$d !== 'undefined' && searchDateRange[0].$d) {
    dateRange.push(new Date(searchDateRange[0].$d))
  } else {
    dateRange.push(new Date(searchDateRange[0] as unknown as Date))
  }

  // Handle end date - always set to end of day (23:59:59.999)
  const endDateSource =
    typeof searchDateRange[1].$d !== 'undefined' && searchDateRange[1].$d
      ? searchDateRange[1].$d
      : (searchDateRange[1] as unknown as Date)
  const endDate = new Date(endDateSource)
  endDate.setHours(23, 59, 59, 999)
  dateRange.push(endDate)

  return dateRange
}
